// DP-1855: Vanilla answer-page bootstrap.
//
// This app's ONLY job is: read the hostname -> fetch the widget external-id ->
// inject the Dappier widget loader script -> let the widget take over #root.
// Previously that was a React + react-router app (~193 KB of react-dom/router
// shipped just to run the ~50 lines below). Rewriting it as plain JS drops all
// of react/react-dom/react-router from the bundle (they're no longer imported,
// so CRA/webpack tree-shakes them out), leaving a few-KB main.js.
//
// Kept identical to the old React component's behavior: hostname-derived env,
// retry with exponential backoff + timeout, CSP-nonce discovery, loading/error
// UI, single-load guard, and #root takeover.

(function () {
  var FETCH_TIMEOUT_MS = 10000;
  var MAX_RETRY_ATTEMPTS = 3;
  var INITIAL_RETRY_DELAY_MS = 1000;

  // Derive environment from the answer-page hostname. Dev/staging answer pages
  // are served under *.dev.dappier.ai / *.staging.dappier.ai; production is the
  // bare custom/Dappier domain. (Old code read this from a build-swapped config
  // module; deriving it here keeps the bootstrap dependency-free.)
  function resolveEnv() {
    var h = window.location.hostname;
    if (/(^|\.)dev\./.test(h) || h.indexOf('.dev.') !== -1) return 'dev';
    if (/(^|\.)staging\./.test(h) || h.indexOf('.staging.') !== -1) return 'staging';
    return 'production';
  }

  var env = resolveEnv();
  var apiUrl = env === 'production' ? 'https://api.dappier.com' : 'https://api.' + env + '.dappier.com';
  var assetsUrl = env === 'production' ? 'https://assets.dappier.com' : 'https://assets-' + env + '.dappier.com';

  var rootEl = document.getElementById('root');

  function showMessage(title, body) {
    if (!rootEl) return;
    rootEl.innerHTML =
      '<div style="display:flex;justify-content:center;align-items:center;height:100vh;' +
      'flex-direction:column;font-family:Arial,sans-serif">' +
      (title ? '<h2>' + title + '</h2>' : '') +
      (body ? '<p>' + body + '</p>' : '') +
      '</div>';
  }

  function getCSPNonce() {
    var meta = document.querySelector('meta[name="csp-nonce"]');
    if (meta && meta.content && meta.content.trim()) return meta.content.trim();
    if (window.__CSP_NONCE__ && String(window.__CSP_NONCE__).trim()) return String(window.__CSP_NONCE__).trim();
    return null;
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function shouldRetry(response) {
    // Don't retry 4xx (except 408 Request Timeout / 429 Too Many Requests);
    // retry network errors, timeouts, and 5xx.
    if (response && response.status >= 400 && response.status < 500) {
      return response.status === 408 || response.status === 429;
    }
    return true;
  }

  // One fetch attempt with an abort-based timeout. Kept as its own function so
  // the timeout closure isn't declared inside the retry loop (no-loop-func).
  function fetchOnce(url) {
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);
    return fetch(url, { signal: controller.signal }).finally(function () { clearTimeout(timeoutId); });
  }

  async function fetchWidgetExternalId(subdomain) {
    var url = apiUrl + '/app/widget/external-id/' + subdomain;
    var lastError;
    for (var attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        var response = await fetchOnce(url);

        if (!response.ok) {
          var httpErr = new Error('HTTP error! status: ' + response.status);
          if (attempt < MAX_RETRY_ATTEMPTS && shouldRetry(response)) {
            lastError = httpErr;
            console.warn('Attempt ' + attempt + ' failed with status ' + response.status + ', retrying...');
            await delay(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1));
            continue;
          }
          throw httpErr;
        }

        var data = await response.json();
        if (data.external_ids && data.external_ids.length > 0) return data.external_ids[0];
        throw new Error('No external IDs found in response');
      } catch (error) {
        var isTimeout = error.name === 'AbortError';
        // fetch() rejects with a TypeError on DNS/offline/connection-refused —
        // transient network failures the retry loop is meant to handle. (The
        // non-retriable HTTP errors were already thrown above with name 'Error'.)
        var isNetwork = error.name === 'TypeError';
        lastError = isTimeout ? new Error('Request timeout after ' + FETCH_TIMEOUT_MS + 'ms') : error;
        if (attempt < MAX_RETRY_ATTEMPTS && (isTimeout || isNetwork)) {
          console.warn('Attempt ' + attempt + ' failed: ' + lastError.message + ', retrying...');
          await delay(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1));
          continue;
        }
        break;
      }
    }
    console.error('Failed to fetch widget external ID after all retry attempts:', lastError);
    throw lastError;
  }

  var widgetLoaded = false;
  function loadWidget(widgetId) {
    if (widgetLoaded) return;
    var cspNonce = getCSPNonce();

    var loaderScript = document.createElement('script');
    loaderScript.src = assetsUrl + '/widget/dappier-loader.min.js';
    loaderScript.async = true;
    if (cspNonce) loaderScript.setAttribute('nonce', cspNonce);
    loaderScript.setAttribute('widget-id', widgetId);
    loaderScript.setAttribute('version', '2.0.0');
    loaderScript.setAttribute('variant', 'answer_page');
    if (env !== 'production') loaderScript.setAttribute('env', env);

    loaderScript.onload = function () {
      console.log('Loader script loaded successfully.');
      var widgetElement = document.createElement('dappier-ask-ai-widget');
      widgetElement.setAttribute('widget-id', widgetId);
      widgetElement.setAttribute('variant', 'answer_page');
      if (rootEl) {
        rootEl.innerHTML = '';
        rootEl.appendChild(widgetElement);
      } else {
        document.body.appendChild(widgetElement);
      }
      widgetLoaded = true;
    };
    loaderScript.onerror = function () {
      console.error('Failed to load the Dappier widget loader script.');
      showMessage('Error loading Answer Page', 'Failed to load the Dappier widget loader script.');
    };

    document.body.appendChild(loaderScript);
  }

  async function init() {
    showMessage('', 'Loading...');
    var subdomain = window.location.hostname;
    if (!subdomain) {
      showMessage('Error loading Answer Page', 'No subdomain found in URL');
      return;
    }
    try {
      var widgetId = await fetchWidgetExternalId(subdomain);
      loadWidget(widgetId);
    } catch (error) {
      showMessage('Error loading Answer Page', error && error.message ? error.message : 'Unknown error');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
